<template>
  <div class="meal-plan">
    <div v-show="!isLoading" class="meal-plan__body">
      <dl>
        <RecordRow label="Status">
          <span data-test="meal-plan-status" class="meal-plan__status" :class="statusChipClass">{{ statusLabel }}</span>
        </RecordRow>
        <RecordRow v-if="planSummary" label="Plan">{{ planSummary }}</RecordRow>
        <RecordRow v-if="nextBillingDate" label="Next billing">{{ nextBillingDate }}</RecordRow>
        <RecordRow label="Meals remaining">{{ mealPlanSubscription.attributes.remaining_meal_credits }}</RecordRow>
      </dl>
      <div v-if="canEdit" class="meal-plan__foot">
        <RecordButton v-if="isPaused" data-test="resume-plan" size="sm" @click="handleResume">Resume</RecordButton>
        <RecordButton v-else data-test="pause-plan" size="sm" @click="handlePause">Pause next cycle</RecordButton>
        <RecordButton data-test="refund-plan" size="sm" @click="openRefundModal">Refund</RecordButton>
        <RecordButton data-test="cancel-plan" variant="danger" size="sm" @click="showCancelModal = true">
          Cancel plan
        </RecordButton>
      </div>
    </div>
    <div v-show="isLoading" class="flex justify-center py-6">
      <BasicSpinner color="gray-900" />
    </div>

    <ConfirmModal
      :is-open="showCancelModal"
      title="Cancel this meal plan?"
      :message="`${customerName} moves back to your default tier.`"
      confirm-label="Cancel plan"
      cancel-label="Keep plan"
      @confirm="handleCancelConfirm"
      @close="showCancelModal = false"
    />

    <TheModal v-model:is-open="showRefundModal" :modal-title="`Refund a meal plan payment for ${customerName}`">
      <template #main>
        <p class="text-sm text-gray-600">Payments {{ customerName }} have made towards this meal plan.</p>
        <div class="mt-4">
          <MultiSelectInput
            v-model="selectedPayments"
            :options="paymentOptions"
            placeholder="Select payments to refund"
            :show-select-all="paymentOptions.length > 1"
          />
        </div>
      </template>
      <template #footer>
        <button class="btn-ghost justify-center" @click="showRefundModal = false">Cancel</button>
        <button
          class="btn-primary justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!selectedPayments.length"
          @click="handleRefundConfirm"
        >
          Refund
        </button>
      </template>
    </TheModal>
  </div>
</template>

<script>
import { computed, defineComponent, ref } from 'vue'

import BasicSpinner from '@/components/BasicSpinner.vue'
import ConfirmModal from '@/components/ConfirmModal.vue'
import RecordButton from '@/components/customers/record/RecordButton.vue'
import RecordRow from '@/components/customers/record/RecordRow.vue'
import MultiSelectInput from '@/components/neue/MultiSelectInput.vue'
import TheModal from '@/components/TheModal.vue'
import { useToast } from '@/composables/useToast'
import api from '@/store/api'
import { mapRelationship, mapType } from '@/store/mappers'

const mediumFormat = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format

const STATUS_LABELS = {
  active: 'Active',
  past_due: 'Past Due',
  pending_first_payment: 'Pending First Payment',
  paused: 'Paused',
  cancelled: 'Cancelled'
}

const STATUS_CLASSES = {
  active: 'bg-green-100 text-green-800',
  past_due: 'bg-red-100 text-red-800',
  pending_first_payment: 'bg-yellow-100 text-yellow-800',
  paused: 'bg-gray-200 text-gray-800',
  cancelled: 'bg-gray-200 text-gray-800'
}

export default defineComponent({
  name: 'MealPlanInfoCard',
  components: {
    BasicSpinner,
    ConfirmModal,
    TheModal,
    MultiSelectInput,
    RecordButton,
    RecordRow
  },
  props: {
    mealPlanSubscription: {
      type: Object,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    customerName: {
      type: String,
      required: true
    },
    conversationUserPairId: {
      type: String,
      required: true
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    mealPlanSubscriptionIds: {
      type: Array,
      default: () => []
    }
  },
  emits: ['pause-plan', 'resume-plan', 'cancel-plan'],
  setup(props, { emit }) {
    const { showToast } = useToast()
    const showCancelModal = ref(false)
    const selectedPayments = ref([])
    const refundModalOpen = ref(false)

    const showRefundModal = computed({
      get: () => refundModalOpen.value,
      set: val => {
        refundModalOpen.value = val
        if (!val) {
          selectedPayments.value = []
        }
      }
    })

    // Skipped_through marks the end of the current unpaid/skipped billing
    // window; status itself stays "active" while paused (pause_next_cycle!
    // does not change status), so the paused chip is derived here rather
    // than read directly off attributes.status.
    const isPaused = computed(() => {
      const skippedThrough = props.mealPlanSubscription?.attributes?.skipped_through
      if (!skippedThrough) return false
      const todayStr = new Date().toISOString().slice(0, 10)
      return skippedThrough >= todayStr
    })

    const displayStatus = computed(() => {
      if (isPaused.value) return 'paused'
      return props.mealPlanSubscription?.attributes?.status
    })

    const statusLabel = computed(() => STATUS_LABELS[displayStatus.value] || displayStatus.value)
    const statusChipClass = computed(() => STATUS_CLASSES[displayStatus.value] || 'bg-gray-200 text-gray-800')

    const nextBillingDate = computed(() => {
      const date = props.mealPlanSubscription?.attributes?.next_payment_date
      if (!date) return null
      return mediumFormat(new Date(date))
    })

    const mealPlan = computed(() => {
      const rel = props.mealPlanSubscription?.relationships?.meal_plan?.data
      return rel ? mapRelationship(rel) : null
    })

    const planSummary = computed(() => {
      const attrs = mealPlan.value?.attributes
      if (!attrs) return null
      const interval = (attrs.billing_interval || '').replace(/_/g, ' ')
      return `${attrs.item_count} meals / ${interval} - ${attrs.price_formatted}`
    })

    const payments = computed(() =>
      mapType('meal_plan_payment').filter(payment =>
        props.mealPlanSubscriptionIds.some(id => String(id) === String(payment.attributes.meal_plan_subscription_id))
      )
    )

    const getPayments = () => {
      api.call('GET', `conversation_user_pairs/${props.conversationUserPairId}/meal_plan_subscriptions/payments`)
    }

    const paymentOptions = computed(() => {
      return payments.value.map(payment => ({
        name: `${mediumFormat(new Date(payment.attributes.created_at))} - ${payment.attributes.amount_formatted}${
          payment.attributes.is_refunded ? ' (Refunded)' : ''
        }`,
        value: payment.id,
        disabled: payment.attributes.is_refunded
      }))
    })

    const openRefundModal = () => {
      getPayments()
      refundModalOpen.value = true
    }

    const handlePause = () => {
      emit('pause-plan', props.mealPlanSubscription.id)
    }

    const handleResume = () => {
      emit('resume-plan', props.mealPlanSubscription.id)
    }

    const handleCancelConfirm = () => {
      emit('cancel-plan', props.mealPlanSubscription.id)
      showCancelModal.value = false
    }

    const handleRefundConfirm = () => {
      selectedPayments.value.forEach(paymentId => {
        api
          .call(
            'POST',
            `conversation_user_pairs/${props.conversationUserPairId}/meal_plan_subscriptions/payments/${paymentId}/refund`
          )
          .then(() => getPayments())
          .catch(() => showToast("Couldn't refund that payment. Please try again.", { type: 'error' }))
      })
      showRefundModal.value = false
    }

    return {
      showCancelModal,
      showRefundModal,
      selectedPayments,
      paymentOptions,
      isPaused,
      statusLabel,
      statusChipClass,
      nextBillingDate,
      planSummary,
      openRefundModal,
      handlePause,
      handleResume,
      handleCancelConfirm,
      handleRefundConfirm
    }
  }
})
</script>

<style scoped>
.meal-plan,
.meal-plan__body {
  display: flex;
  flex: 1;
  flex-direction: column;
}
.meal-plan__status {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 2px 9px;
  font-size: 12px;
  font-weight: 600;
}
.meal-plan__foot {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: auto;
  padding-top: 14px;
}
</style>
